/**
 * PATH: packages/tenant-shared/src/settings/settingsHubConfig.ts
 * FILE_ID: TENANTSHARED-SETTINGS-HUB-CONFIG-001
 * PURPOSE: Tenant-scoped runtime configuration for the SettingsPage —
 *          a single setter (`setSettingsHubConfig`) registered once at
 *          tenant boot, read by the canonical SettingsPage body to
 *          decide which optional surfaces to render.
 * WHY:     Pre-cutover, `apps/<tenant>/src/features/settings/SettingsPage.tsx`
 *          had several tenant-specific divergences:
 *
 *            1. Gmail (Google Workspace) integration card on the
 *               Integrations tab (newmark-only — BUG-0011)
 *            2. Audit Forwarder tab + content (newmark-only — sam
 *               retired the endpoint in qb!2368)
 *            3. Sender-name placeholder copy ("Newmark Constructions"
 *               vs "Sydney Architectural Metal")
 *            4. (consequence of 1) `useGmailStatus` import gated
 *
 *          (S1 also briefly considered a `workersPanel` slot for
 *          newmark to opt out, but in practice both tenants render
 *          `<WorkersPanel/>` inline — S2's lift mounts it
 *          unconditionally inside `<SettingsPage/>`, so no flag is
 *          needed. The slot was removed by S2 cleanup to avoid a
 *          dead config knob.)
 *
 *          The divergence meant a fix on one tenant silently failed to
 *          reach the other. The Settings cutover arc converges the two
 *          tenant bodies onto a SINGLE byte-identical canonical body
 *          and lifts every per-tenant decision into the runtime config
 *          object below. Each tenant calls `setSettingsHubConfig({...})`
 *          ONCE at boot before the first SettingsModal mount; the
 *          canonical body reads the registered flags via
 *          `getSettingsHubConfig()` whenever it needs to decide
 *          whether to render an optional surface.
 *
 * FLOW:
 *   ┌─────────────────────────┐   ┌─────────────────────────┐   ┌────────────────────┐
 *   │ apps/<tenant>/main.tsx  │ → │ setSettingsHubConfig    │ → │ apps/<tenant>/src/ │
 *   │ at module-eval time     │   │ stores in module        │   │ features/settings/ │
 *   │                         │   │ singleton               │   │ SettingsPage.tsx   │
 *   └─────────────────────────┘   └─────────────────────────┘   │ getSettingsHub-    │
 *                                                                │ Config() each      │
 *                                                                │ render             │
 *                                                                └────────────────────┘
 *
 * RELATED:
 *   - apps/{newmark,sam}/src/features/settings/SettingsPage.tsx — primary
 *     consumer, byte-identical between tenants post-cutover.
 *   - apps/{newmark,sam}/src/main.tsx — registration site.
 *   - docs/architecture/EMAIL_KANBAN_CONSOLIDATION_STATUS.md — the
 *     "Settings reconcile" section explains the new operating model.
 *   - docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md — section S1 records
 *     this MR's decisions.
 *
 * TESTS:
 *   - Default config is the safest "everything off + neutral placeholder",
 *     so a tenant that forgets to call `setSettingsHubConfig` still
 *     renders without 404s or undefined-flag crashes.
 *   - `setSettingsHubConfig` is idempotent within a tenant build (calling
 *     it twice with the same payload is a no-op). Calling with a partial
 *     object MERGES into the existing config — the registration site is
 *     intentionally NOT a "replace whole config" interface to keep
 *     mid-arc additions backward-compatible (a future MR can register
 *     a new flag without forcing every tenant boot to add it).
 */

export interface SettingsHubConfig {
  /**
   * If `true`, renders the Gmail (Google Workspace) integration card
   * on the Integrations tab, peer to the existing Microsoft Outlook
   * card. Default `false` because the Gmail backend uses a per-tenant
   * Google Cloud OAuth client that's provisioned via the privileged
   * 0x3admin staff portal — tenants without that provisioning would
   * see a permanently-disconnected card.
   *
   * Reported by Newmark on 2026-05-11 (BUG-0011): "after granting
   * Gmail OAuth permissions I was redirected to Settings →
   * Integrations with NO Google entry visible — the page only showed
   * Xero + Microsoft Outlook, so I assumed the connection had silently
   * failed". The card surfaces:
   *   - Connection status (connected / expired / not connected)
   *   - Connect / Reconnect button (kicks off the OAuth flow)
   *   - "Custom Google OAuth app" footer note pointing operators at
   *     support@quotech.ai for credential rotation.
   */
  showGmailIntegration?: boolean

  /**
   * If `true`, registers the Audit Forwarder tab in the SettingsPage
   * tab list AND renders its content panel (a health chip + recent-
   * events table backed by `/api/system/audit-forwarder/health`).
   * Default `false`.
   *
   * Sam retired the HTTP-POST audit forwarder stub in qb!2368
   * (PR 7C-3, 2026-04-30) so its endpoint returns 404 — keeping the
   * tab visible on sam meant operators saw "Request failed with
   * status code 404" on every open. Newmark still has the forwarder
   * configured + the endpoint live, so the tab is operationally
   * useful there.
   *
   * When the future cross-tenant audit ledger lands as a Django app
   * with a shared `audit_event` table, both tenants will set this to
   * `true` and the rendered content will switch to the new shape.
   */
  showAuditForwarderTab?: boolean

  /**
   * Placeholder text for the "Sender name" input under the Email
   * preferences card. Tenants set this to a recognisable example of
   * their own brand name so operators understand what shape of value
   * the field accepts.
   *
   * Examples:
   *   - sam:     'e.g. Sydney Architectural Metal'
   *   - newmark: 'e.g. Newmark Constructions'
   *
   * Default: 'e.g. Acme Construction' (generic neutral fallback).
   */
  senderNamePlaceholder?: string

  /**
   * When false, the universal Settings tab list omits All Kanban.
   * Default true so All-Kanban pages are reachable without a board.
   */
  showAllKanbanTab?: boolean

  /**
   * Host-relative organisation settings destination. Empty hides the
   * outbound org links. Never a hardcoded dashboard hostname.
   */
  organisationSettingsHref?: string

  /**
   * Host-relative team / members destination. Empty hides Org members.
   * Never a hardcoded dashboard hostname.
   */
  teamSettingsHref?: string

  /**
   * Host-relative email settings destination. Empty hides the outbound
   * email sync link. Never a hardcoded email hostname.
   */
  emailSettingsHref?: string
}

const DEFAULTS: Required<SettingsHubConfig> = {
  showGmailIntegration: false,
  showAuditForwarderTab: false,
  senderNamePlaceholder: 'e.g. Acme Construction',
  showAllKanbanTab: true,
  organisationSettingsHref: '/settings?tab=organisation',
  teamSettingsHref: '/settings?tab=team',
  emailSettingsHref: '/settings?tab=email',
}

let _config: Required<SettingsHubConfig> = { ...DEFAULTS }

/**
 * Register tenant-specific settings flags. Called ONCE at tenant boot
 * (typically in apps/<tenant>/src/main.tsx) before the first render.
 *
 * Calling with a partial object MERGES into the existing config so
 * future MRs can add a new flag without breaking tenants that haven't
 * been updated to register it. Calling with the same payload twice is
 * a no-op (idempotent).
 *
 * Returns the merged config so call sites can debug-log it during
 * boot if they want.
 */
export function setSettingsHubConfig(
  partial: SettingsHubConfig
): Required<SettingsHubConfig> {
  _config = { ..._config, ...partial }
  return _config
}

/**
 * Read the current registered config. Safe to call before
 * `setSettingsHubConfig` runs (returns DEFAULTS).
 *
 * NOTE: This is NOT a React hook — it returns a plain object. Read
 * inside a render to get the current value, but do not store the
 * reference long-term across re-renders if a setter has fired in
 * between (the singleton is mutated in-place via a NEW object
 * reference each setter call, so React reads always see the current
 * value).
 */
export function getSettingsHubConfig(): Required<SettingsHubConfig> {
  return _config
}

/**
 * Reset to defaults. Used by unit tests to keep test isolation —
 * calling between tests prevents one test's `setSettingsHubConfig`
 * call from leaking into the next test.
 */
export function _resetSettingsHubConfigForTesting(): void {
  _config = { ...DEFAULTS }
}
